import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Check, Clock, Video } from "lucide-react";
import { bookingCancel, bookingCreate, bookingMine, bookingSlots, errorMessage } from "./api";

const ymd = (d) => d.toLocaleDateString("en-CA");                                   // YYYY-MM-DD in the visitor's own time zone
const dayLabel = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
const timeLabel = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
const fullLabel = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "numeric", minute: "2-digit" });
const browserTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ""; } };

// Pick a day and time for the product walkthrough. Works right after the sign-up form (private link token) and, later,
// from the dashboard (signed in). Booking puts the demo on the booker's calendar: Google emails them an invitation.
export default function DemoBooking({ token, auth, leadId, onChange }) {
  const [mine, setMine] = useState(null);
  const [error, setError] = useState(null);
  const [from, setFrom] = useState(() => new Date());
  const [slots, setSlots] = useState(null);
  const [day, setDay] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);
  const [justBooked, setJustBooked] = useState(false);

  const loadMine = useCallback(() => bookingMine(token, auth, leadId).then(setMine).catch((e) => setError(errorMessage(e, "This booking link isn't valid."))), [token, auth, leadId]);
  useEffect(() => { loadMine(); }, [loadMine]);

  const loadSlots = useCallback(() => {
    setSlots(null); setPick(null);
    return bookingSlots(token, auth, ymd(from), 7, leadId).then((d) => { setSlots(d.slots); setDay((cur) => (cur && d.slots.some((s) => ymd(new Date(s)) === cur) ? cur : null)); })
      .catch((e) => toast.error(errorMessage(e, "Couldn't load the available times")));
  }, [token, auth, from, leadId]);
  useEffect(() => { if (mine && (!mine.booking || changing)) loadSlots(); }, [mine, changing, loadSlots]);

  const byDay = useMemo(() => {
    const m = {};
    (slots || []).forEach((s) => { (m[ymd(new Date(s))] = m[ymd(new Date(s))] || []).push(s); });
    return m;
  }, [slots]);
  const days = Object.keys(byDay).sort();
  const selectedDay = day && byDay[day] ? day : days[0];

  const book = async () => {
    setBusy(true);
    try {
      await bookingCreate(token, auth, pick, leadId);
      toast.success("Your demo is booked");
      setJustBooked(true); setChanging(false); await loadMine(); if (onChange) onChange();
    } catch (e) { toast.error(errorMessage(e, "Couldn't book that time")); loadSlots(); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel your demo booking?")) return;
    try { await bookingCancel(token, auth, leadId); toast.success("Booking cancelled"); setJustBooked(false); await loadMine(); if (onChange) onChange(); } catch (e) { toast.error(errorMessage(e)); }
  };

  if (error) return <div className="text-sm text-red-700" data-testid="booking-error">{error}</div>;
  if (!mine) return <div className="text-forest-700">Loading…</div>;
  const b = mine.booking;
  const tz = browserTz();
  const maxFrom = new Date(Date.now() + (mine.max_days_ahead || 30) * 86400000);

  if (b && !changing) {
    const cal = b.calendar || {};
    return (
      <div className="space-y-3 rounded-2xl border border-forest-600/30 bg-forest-600/5 p-5" data-testid="booking-done">
        <div className="flex items-center gap-2 font-display font-semibold text-forest-700"><Check className="h-5 w-5" />{justBooked ? "You're booked" : "Your demo is booked"}</div>
        <div className="text-lg font-semibold text-forest-700" data-testid="booking-when">{fullLabel(b.start)}</div>
        <div className="text-xs text-foreground/60">Shown in your time zone{tz ? ` (${tz})` : ""}.</div>
        <p className="text-sm text-foreground/80" data-testid="booking-calendar-note">
          {cal.status === "created" ? <>A Google Calendar invitation was sent to <strong>{mine.request.work_email}</strong>{leadId ? " and to the school inbox" : ""}. Accept it to add the demo to your calendar.</>
            : <>We&apos;ve emailed you a confirmation with a calendar file attached to <strong>{mine.request.work_email}</strong>.</>}</p>
        {cal.meet_link && <a className="inline-flex items-center gap-2 text-sm text-forest-700 underline" href={cal.meet_link} target="_blank" rel="noreferrer" data-testid="booking-meet"><Video className="h-4 w-4" />Google Meet link</a>}
        {leadId && cal.html_link && <div><a className="text-sm text-forest-700 underline" href={cal.html_link} target="_blank" rel="noreferrer" data-testid="booking-calendar-link">Open in Google Calendar</a></div>}
        {leadId && cal.status === "failed" && <div className="text-sm text-red-700" data-testid="booking-calendar-failed">The Google Calendar event could not be created. The booking is saved; add the meeting by hand.</div>}
        <div className="flex gap-3 pt-1">
          <button type="button" className="btn-ghost border border-sand-300 text-sm" onClick={() => setChanging(true)} data-testid="booking-reschedule">Change time</button>
          <button type="button" className="btn-ghost border border-sand-300 text-sm" onClick={cancel} data-testid="booking-cancel">Cancel booking</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="demo-booking">
      <div className="flex items-center gap-2 font-display font-semibold text-forest-700"><CalendarDays className="h-5 w-5" />{b ? "Choose a new time" : "Choose a time for your product walkthrough"}</div>
      <div className="text-xs text-foreground/60" data-testid="booking-tz">About {mine.slot_minutes} minutes. Times are shown in your time zone{tz ? ` (${tz})` : ""}.</div>
      <section className="space-y-3" data-testid="section-pick-day">
        <div className="flex items-center gap-2 font-display font-semibold text-forest-700"><CalendarDays className="h-5 w-5" />Choose a day</div>
        <div className="flex items-center justify-between text-sm">
          <button type="button" className="text-forest-700 disabled:opacity-40" disabled={ymd(from) <= ymd(new Date())} onClick={() => setFrom((d) => new Date(Math.max(Date.now(), d.getTime() - 7 * 86400000)))} data-testid="booking-prev">&larr; Earlier</button>
          <span className="text-foreground/60">{dayLabel(from.toISOString())} &ndash; {dayLabel(new Date(from.getTime() + 6 * 86400000).toISOString())}</span>
          <button type="button" className="text-forest-700 disabled:opacity-40" disabled={new Date(from.getTime() + 7 * 86400000) > maxFrom} onClick={() => setFrom((d) => new Date(d.getTime() + 7 * 86400000))} data-testid="booking-next">Later &rarr;</button>
        </div>
        {!slots && <div className="text-forest-700 text-sm">Loading available times…</div>}
        {slots && !days.length && <div className="rounded-xl bg-sand-100 p-4 text-sm" data-testid="booking-none">No times are free in this week. Try the next one{mine.calendar_connected ? "" : ", or reply to our email and we'll arrange a time"}.</div>}
        {days.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="booking-days">
            {days.map((d) => (
              <button key={d} type="button" data-testid={`booking-day-${d}`} onClick={() => { setDay(d); setPick(null); }}
                className={`rounded-xl border px-3 py-2 text-sm ${selectedDay === d ? "bg-forest-700 text-sand-100 border-forest-700" : "bg-white border-sand-300 text-forest-700"}`}>{dayLabel(byDay[d][0])}</button>))}
          </div>)}
      </section>
      {days.length > 0 && (
        <section className="space-y-3" data-testid="section-pick-time">
          <div className="flex items-center gap-2 font-display font-semibold text-forest-700"><Clock className="h-5 w-5" />Choose a time <span className="text-xs font-normal text-foreground/60">on {dayLabel(byDay[selectedDay][0])}</span></div>
          <div className="flex flex-wrap gap-2" data-testid="booking-slots">
            {byDay[selectedDay].map((s, i) => (
              <button key={s} type="button" data-testid={`booking-slot-${i}`} onClick={() => setPick(s)}
                className={`rounded-xl border px-4 py-2 text-sm ${pick === s ? "bg-forest-700 text-sand-100 border-forest-700" : "bg-white border-sand-300 text-forest-700 hover:border-forest-600"}`}>{timeLabel(s)}</button>))}
          </div>
        </section>)}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn-primary disabled:opacity-50" disabled={!pick || busy} onClick={book} data-testid="booking-confirm">
          {busy ? "Booking…" : pick ? `Book ${dayLabel(pick)} at ${timeLabel(pick)}` : "Pick a time"}</button>
        {b && changing && <button type="button" className="text-sm text-forest-700 underline" onClick={() => setChanging(false)}>Keep my current time</button>}
      </div>
    </div>
  );
}
